import { APP_BASE_HREF } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { map, type Observable, switchMap, take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { ClipboardCopy, DialogService, WindowRef } from '@core/user-interface-legacy';
import { AskTemplatesActions } from '@tellent-ask-hr/templates-data';

import { AskTemplatesDataService } from './ask-templates-data.service';

@Injectable({ providedIn: 'root' })
export class AskTemplatesService {
  private readonly dialogService = inject(DialogService);
  private readonly templatesDataService = inject(AskTemplatesDataService);
  private readonly i18n = inject(I18nService);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly clipboardCopy = inject(ClipboardCopy);
  private readonly windowRef = inject(WindowRef);
  private readonly baseUrl = inject(APP_BASE_HREF);

  public getViewRefreshEvents$(): Observable<void> {
    return this.actions$.pipe(
      ofType(
        AskTemplatesActions.createOnePessimisticSuccess,
        AskTemplatesActions.updateOnePessimisticSuccess,
        AskTemplatesActions.removeOnePessimisticSuccess,
        AskTemplatesActions.duplicateTemplateSuccess,
      ),
      map(() => void 0),
    );
  }

  public confirmDelete(templateId: number): Observable<boolean> {
    return this.dialogService
      .error({
        title: this.i18n.translate('tlc.ask_hr.forms.delete_form_heading'),
        message: this.i18n.translate('tlc.ask_hr.forms.delete_form_description'),
      })
      .pipe(
        take(1),
        filterBoolean,
        switchMap(isSuccess =>
          isSuccess
            ? this.templatesDataService
                .deleteTemplate(templateId)
                .pipe(map(result => result.isSuccess))
            : [false],
        ),
      );
  }

  public copyTemplateLink(templateId: number) {
    const url = this.router.createUrlTree([
      {
        outlets: {
          primary: ['ask-hr', 'my', 'forms'],
          dialog: ['ask-hr-create-ticket', templateId],
        },
      },
    ]);
    const fullUrl = `${this.windowRef.nativeWindow.location.origin}${this.baseUrl}${url.toString()}`;
    this.clipboardCopy.fromString(fullUrl);
  }

  public navigateToCreateTicket(templateId: number) {
    const urlTree = this.router.createUrlTree(
      [
        {
          outlets: {
            dialog: ['ask-hr-create-ticket', templateId],
          },
        },
      ],
      { relativeTo: this.route },
    );
    this.router.navigateByUrl(urlTree);
  }

  public duplicateTemplate(templateId: number): Observable<number | null> {
    return this.templatesDataService.loadTemplateById(templateId).pipe(
      switchMap(({ isSuccess, success }) => {
        if (isSuccess) {
          const { id: _1, createdAt: _2, updatedAt: _3, autoAssignees, ...template } = success!;
          return this.templatesDataService
            .duplicateTemplate({
              ...template,
              active: false,
              name: `${template.name} (${this.i18n.translate('tlc.ask_hr.forms.duplicate_added_suffix')})`,
              autoAssignments: autoAssignees.map(assignee => ({ memberGuid: assignee.guid })),
            })
            .pipe(map(result => (result.isSuccess ? result.success!.id : null)));
        } else {
          return [null];
        }
      }),
    );
  }
}
